import { debounce } from "lodash";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useRecoilValue } from "recoil";

import { useRideNearbyActions } from "@ryde/redux";
import {
  nearbyOtherRidesIdsBatchCacheSelector,
  specificTableRowIdsRenderedAtom,
  useSystemParams,
} from "@ryde/shared";

const DEBOUNCE_API_FETCH_TIME = 300;

export const RideResolveNearBy = memo(() => {
  const { fetchNearbyRidesBatch } = useRideNearbyActions();
  const nearbyOtherRidesIdsBatchCache = useRecoilValue(
    nearbyOtherRidesIdsBatchCacheSelector,
  );
  const { disableNearByFeature } = useSystemParams();
  const isNearByDisabled = disableNearByFeature ?? false;

  // Additional cache to avoid fetching the same rides multiple times due to the race-condition
  // specific ids we want to fetch
  const pendingIdsRef = useRef<Set<number>>(new Set());
  // specific ids that are fetching
  const inFlightIdsRef = useRef<Set<number>>(new Set());
  const specificTableRowIdsRendered = useRecoilValue(
    specificTableRowIdsRenderedAtom,
  );

  const cachedDiff = useMemo(() => {
    const allProcessedIds = new Set([
      ...nearbyOtherRidesIdsBatchCache,
      ...pendingIdsRef.current,
      ...inFlightIdsRef.current,
    ]);

    return specificTableRowIdsRendered.difference(allProcessedIds);
  }, [nearbyOtherRidesIdsBatchCache, specificTableRowIdsRendered]);

  const processPendingIds = useCallback(async (): Promise<void> => {
    if (pendingIdsRef.current.size === 0) {
      return;
    }

    const idsToProcess = [...pendingIdsRef.current];
    pendingIdsRef.current.clear();

    idsToProcess.forEach(id => inFlightIdsRef.current.add(id));

    try {
      await fetchNearbyRidesBatch(idsToProcess);
    } finally {
      idsToProcess.forEach(id => inFlightIdsRef.current.delete(id));
    }
  }, [fetchNearbyRidesBatch]);

  const debouncedProcessPendingIds = useMemo(
    () => debounce(processPendingIds, DEBOUNCE_API_FETCH_TIME),
    [processPendingIds],
  );

  useEffect(() => {
    const pendingIds = pendingIdsRef.current;
    const inFlightIds = inFlightIdsRef.current;

    return () => {
      pendingIds.clear();
      inFlightIds.clear();
      debouncedProcessPendingIds.cancel();
    };
  }, [debouncedProcessPendingIds]);

  useEffect(() => {
    // When the Near-By feature is switched off, skip batch fetching entirely
    // so the rides screen does not fire unneeded nearby API calls.
    if (isNearByDisabled || cachedDiff.size === 0) {
      return;
    }

    let hasNewIds = false;
    cachedDiff.forEach(id => {
      if (!pendingIdsRef.current.has(id)) {
        pendingIdsRef.current.add(id);
        hasNewIds = true;
      }
    });

    if (hasNewIds) {
      debouncedProcessPendingIds();
    }
  }, [
    cachedDiff,
    isNearByDisabled,
    debouncedProcessPendingIds,
    specificTableRowIdsRendered,
  ]);

  return null;
});
